import { FilterChip } from "@rosc/ui/v32/chip";
import { Check, Plus } from "lucide-react";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";
import type { DefaultRecordingTemplates } from "@/common/enums/enums";
import { builtInTemplateLabel } from "@/lib/document-name";
import {
  SPECIALTIES,
  type Specialty,
  suggestedTemplates,
} from "@/lib/guided/specialty";
import { cn } from "@/lib/utils";

// Guided first visit (VITE_GUIDED_FIRST_VISIT_ENABLED): the welcome's
// specialty and the templates suggested for it, each with a short preview of
// the note's sections. The first suggestion is the default.

const TEMPLATE_KEY: Record<string, "soap" | "specialist" | "voice"> = {
  "SOAP Template": "soap",
  "Specialist Template": "specialist",
  "Voice to Text Dictation": "voice",
};

export const SpecialtyPicker = ({
  value,
  onChange,
}: {
  value: Specialty | null;
  onChange: (specialty: Specialty) => void;
}): JSX.Element => {
  const { t } = useTranslation();
  return (
    <fieldset
      className="flex min-w-0 flex-wrap gap-2 border-0 p-0"
      data-testid="guided-specialty"
    >
      <legend className="sr-only">{t("guided.welcome.specialty")}</legend>
      {SPECIALTIES.map((specialty) => (
        <FilterChip
          key={specialty}
          data-specialty={specialty}
          pressed={value === specialty}
          onPressedChange={() => onChange(specialty)}
        >
          {t(`guided.specialty.${specialty}`)}
        </FilterChip>
      ))}
    </fieldset>
  );
};

export const TemplateSuggestions = ({
  specialty,
  value,
  onChange,
}: {
  specialty: Specialty | null;
  value: string;
  onChange: (template: DefaultRecordingTemplates) => void;
}): JSX.Element => {
  const { t } = useTranslation();
  const options = suggestedTemplates(specialty);
  return (
    <fieldset
      className="grid min-w-0 gap-2 border-0 p-0"
      data-testid="guided-templates"
    >
      <legend className="sr-only">{t("guided.welcome.templates")}</legend>
      {options.map((template, index) => {
        const key = TEMPLATE_KEY[template] ?? "soap";
        const selected = value === template;
        // One section per line.
        const lines = t(`guided.templates.${key}.preview`)
          .split("\n")
          .filter(Boolean)
          .slice(0, 4);
        return (
          <label
            key={template}
            className={cn(
              "relative flex min-h-11 cursor-pointer items-start gap-3 rounded-[12px] p-3 text-left transition-[box-shadow,background-color] has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-v3-brand",
              selected
                ? "bg-v3-panel shadow-[0_0_0_1.5px_var(--v3-ink),0_1px_2px_rgba(0,0,0,0.06)]"
                : "bg-v3-surface-2 shadow-v3-hairline hover:bg-v3-surface-3",
            )}
            data-selected={selected || undefined}
            data-template={template}
            data-testid="guided-template"
          >
            <input
              checked={selected}
              className="sr-only"
              name="guided-template"
              type="radio"
              value={template}
              onChange={() => onChange(template)}
            />
            <span
              aria-hidden
              className={cn(
                "mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full",
                selected
                  ? "bg-v3-ink text-v3-on-ink"
                  : "shadow-[inset_0_0_0_1.5px_var(--v3-border-strong)]",
              )}
            >
              {selected && <Check className="size-2.5" strokeWidth={3.5} />}
            </span>
            <span className="min-w-0 flex-1">
              <span className="flex flex-wrap items-baseline gap-x-2">
                <span className="text-[14px] font-semibold leading-5 text-v3-text">
                  {builtInTemplateLabel(template)}
                </span>
                <span className="text-[12.5px] leading-5 text-v3-text-3">
                  {index === 0
                    ? t("guided.templates.suggested")
                    : t(`guided.templates.${key}.hint`)}
                </span>
              </span>
              {/* A short preview of the note: its first sections. */}
              <span
                className="mt-1.5 flex flex-wrap gap-1"
                data-testid="guided-template-preview"
              >
                <span className="sr-only">
                  {t("guided.templates.previewLabel")}:{" "}
                </span>
                {lines.map((line) => (
                  <span
                    key={line}
                    className="inline-flex h-5 items-center rounded-md bg-v3-surface-2 px-1.5 text-[11px] font-semibold uppercase tracking-[0.03em] text-v3-text-2 shadow-v3-hairline"
                  >
                    {line}
                  </span>
                ))}
              </span>
            </span>
          </label>
        );
      })}
    </fieldset>
  );
};

// Below the suggestions: the template creator (from the doctor's own notes
// or blank). Finishes the welcome with the choices made so far, then opens it.
export const CreateTemplateLink = ({
  onClick,
}: {
  onClick: () => void;
}): JSX.Element => {
  const { t } = useTranslation();
  return (
    <button
      className="flex min-h-11 w-full items-start gap-3 rounded-[12px] p-3 text-left shadow-[inset_0_0_0_1px_var(--v3-border-strong)] transition-colors [border-style:dashed] hover:bg-v3-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-v3-brand"
      data-testid="guided-create-template"
      type="button"
      onClick={onClick}
    >
      <span
        aria-hidden
        className="mt-0.5 flex size-4 shrink-0 items-center justify-center text-v3-text-2"
      >
        <Plus className="size-4" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[14px] font-semibold leading-5 text-v3-text">
          {t("templates.createOwn.title")}
        </span>
        <span className="block text-[12.5px] leading-5 text-v3-text-3">
          {t("templates.createOwn.hint")}
        </span>
      </span>
    </button>
  );
};
